import { Button, Select, Spinner, TextField } from '@cimpress/react-components';
import { useEffect, useState, type CSSProperties } from 'react';
import {
    CardBuilder,
    Control,
    Page,
    PreviewRow,
    PreviewRows,
    PreviewSection,
    ImageUrlSelector,
    type SelectorOption,
} from '../Components';
import { useCancelableEffect } from '../tools';
import { postStylizeImageUrl } from '../api/stylizedImage';
import { useStylizePhotoParams } from '../Components/StylizePhoto/useStylizePhotoParams';

const LOADING_CONTAINER_STYLE: CSSProperties = {
    textAlign: 'center' as const,
    padding: '20px',
};

const LOADING_TEXT_STYLE: CSSProperties = {
    marginTop: '10px',
};

const BACKGROUND_MODE_OPTIONS: SelectorOption[] = [
    { label: 'Clean', value: 'clean' },
    { label: 'Original', value: 'original' },
    { label: 'Color', value: 'color' },
];

const STYLE_OPTIONS: SelectorOption[] = [
    { label: 'Watercolor', value: 'watercolor' },
    { label: 'Oil Painting', value: 'oil_painting' },
    { label: 'Single Lineart', value: 'single_lineart' },
    { label: 'Comic', value: 'comic' },
    { label: 'Pencil Sketch', value: 'pencil_sketch' },
    { label: 'Photomontage', value: 'photomontage' },
    { label: 'Pixel Art', value: 'pixel_art' },
    { label: 'Ink Sketch', value: 'ink_sketch' },
    { label: 'Crayon Drawing', value: 'crayon_drawing' },
    { label: 'Digital Art', value: 'digital_art' },
    { label: 'Graph Ink', value: 'graph_ink' },
    { label: 'Royal Oil Painting', value: 'royal_oil_painting' },
    { label: 'King Renaissance', value: 'king_renaissance' },
    { label: 'Queen Renaissance', value: 'queen_renaissance' },
    { label: 'Military Renaissance', value: 'military_renaissance' },
];

export const StylizePhotoPage = () => {
    const {
        imageUrl,
        styleValue,
        backgroundModeValue,
        backgroundPrompt,
        setImageUrl,
        setStyleValue,
        setBackgroundModeValue,
        setBackgroundPrompt,
    } = useStylizePhotoParams();
    const [resultRow, setResultRow] = useState(PreviewRow());
    const [isLoading, setIsLoading] = useState(false);
    const [callStylizePhoto, setCallStylizePhoto] = useState(false);
    const [showBackgroundPrompt, setShowBackgroundPrompt] = useState(false);
    const [originalImageRow, setOriginalImageRow] = useState(PreviewRow());
    const [enableStylizePhoto, setEnableStylizePhoto] = useState(false);

    const styleSelectedOption = STYLE_OPTIONS.find((option) => option.value === styleValue) || STYLE_OPTIONS[0];
    const backgroundModeSelectedOption =
        BACKGROUND_MODE_OPTIONS.find((option) => option.value === backgroundModeValue) || BACKGROUND_MODE_OPTIONS[0];

    useEffect(() => {
        if (backgroundModeValue === 'color') {
            setShowBackgroundPrompt(true);
        } else {
            setShowBackgroundPrompt(false);
        }
    }, [backgroundModeValue]);

    useEffect(() => {
        if (imageUrl && imageUrl.trim() !== '') {
            setEnableStylizePhoto(true);
            setResultRow(PreviewRow());
        }
        if (showBackgroundPrompt && !backgroundPrompt) {
            setEnableStylizePhoto(false);
        }
    }, [imageUrl, showBackgroundPrompt, backgroundPrompt]);

    // Create original image row when imageUrl changes
    useEffect(() => {
        if (imageUrl && imageUrl.trim() !== '') {
            const originalCard = CardBuilder.ofType('imageUrl')
                .withTitle('Original Image')
                .withData({ imageUrl: imageUrl })
                .build();
            setOriginalImageRow(PreviewRow(originalCard));
        } else {
            setOriginalImageRow(PreviewRow());
            setResultRow(PreviewRow());
        }
    }, [imageUrl]);
    useCancelableEffect(
        (signal) => {
            (async () => {
                if (callStylizePhoto) {
                    setIsLoading(true);
                    try {
                        const resultImageUrl = await postStylizeImageUrl({
                            body: {
                                imageUrl,
                                style: styleValue,
                                background: {
                                    mode: backgroundModeValue,
                                    prompt: backgroundPrompt,
                                },
                            },
                            signal,
                        });
                        const resultCard = CardBuilder.ofType('imageUrl')
                            .withTitle('Stylized image')
                            .withData({ imageUrl: resultImageUrl })
                            .build();

                        setResultRow(PreviewRow(resultCard));
                    } catch (error) {
                        const errorCard = CardBuilder.ofType('documentUrl')
                            .withTitle(`Error Stylizing Image`)
                            .withLoader(async () => {
                                throw error;
                            })
                            .build();
                        setResultRow(PreviewRow(errorCard));
                    } finally {
                        setIsLoading(false);
                        setCallStylizePhoto(false);
                    }
                }
            })();
        },
        [callStylizePhoto, imageUrl, styleValue, backgroundModeValue, backgroundPrompt],
    );
    function onClickStylizePhoto() {
        setCallStylizePhoto(true);
    }

    return (
        <Page>
            <Page.Title>Stylize Photo</Page.Title>

            <Page.Controls>
                <Control>
                    <Control.Title>Image Url (allowed file types: .jpg, .jpeg and .png)</Control.Title>
                    <ImageUrlSelector
                        url={imageUrl ?? undefined}
                        onChange={(value) => setImageUrl(value ?? '')}
                        allowedFileTypes={['image/jpeg', 'image/png']}
                        setIsImageLoading={() => undefined}
                    />
                </Control>
                <Control spacing="standard">
                    <Control.Title>Style</Control.Title>
                    <Select
                        onChange={(selected) => selected && setStyleValue(selected.value)}
                        value={styleSelectedOption}
                        options={STYLE_OPTIONS}
                        label="Style"
                    />
                </Control>
                <Control spacing="standard">
                    <Control.Title>Background Mode</Control.Title>
                    <Select
                        onChange={(selected) => selected && setBackgroundModeValue(selected.value)}
                        value={backgroundModeSelectedOption}
                        options={BACKGROUND_MODE_OPTIONS}
                        label="Background Mode"
                    />
                </Control>
                {showBackgroundPrompt && (
                    <Control>
                        <Control.Title>Background Prompt</Control.Title>
                        <TextField
                            label="Background Prompt"
                            onChange={(e) => setBackgroundPrompt(e.target.value)}
                            value={backgroundPrompt ?? ''}
                            required
                        />
                    </Control>
                )}
                <Button onClick={onClickStylizePhoto} disabled={!enableStylizePhoto}>
                    Stylize Photo
                </Button>
            </Page.Controls>

            <Page.Content>
                <PreviewSection
                    introduction={
                        'This is a page to test the Stylize Photo endpoint. Fill in the imageUrl or upload an image and click "Stylize Photo" to see stylized results.'
                    }
                    showIntro={!resultRow.length && !originalImageRow.length}
                    rows={PreviewRows(
                        originalImageRow,
                        ...(isLoading
                            ? [
                                  PreviewRow(
                                      CardBuilder.ofType('childComponent')
                                          .withTitle('Processing...')
                                          .withChildComponent(() => (
                                              <div style={LOADING_CONTAINER_STYLE}>
                                                  <Spinner />
                                                  <p style={LOADING_TEXT_STYLE}>Stylizing image...</p>
                                              </div>
                                          ))
                                          .build(),
                                  ),
                              ]
                            : [resultRow]),
                    )}
                />
            </Page.Content>
        </Page>
    );
};

export default StylizePhotoPage;
